import { createFileRoute, Link } from "@tanstack/react-router";
import { Container } from "@/components/site/Container";
import { ButtonLink } from "@/components/site/Button";
import { canonical } from "@/lib/seo";
import { loadPageSeo } from "@/lib/page-seo-loader";
import { applySeoOverrides } from "@/lib/seo-overrides";
import { ArrowRight, Phone } from "lucide-react";

const SLUG = "/services";
const TITLE = "Phoenix Roofing Services | Durafoam Roofing";
const DESCRIPTION =
  "Every roofing service Durafoam offers in the Phoenix Valley — foam, tile, shingle, flat, commercial, and emergency repair. Family-owned since 1989.";

const services = [
  { slug: "/foam-roofing", title: "Foam Roofing Installation", blurb: "Seamless spray foam roofs built for the Valley's heat and monsoon." },
  { slug: "/foam-roof-repair-phoenix", title: "Foam Roof Repair", blurb: "Surgical patches and coating restoration for leaking foam roofs." },
  { slug: "/foam-roof-coating-recoating", title: "Foam Roof Coating & Recoating", blurb: "Add another 5 years to your Phoenix foam roof with an on-time recoat." },
  { slug: "/roof-repair", title: "Roof Repair (All Types)", blurb: "Foam, tile, shingle, flat — diagnosed and repaired right." },
  { slug: "/roof-leak-repair", title: "Roof Leak Repair", blurb: "Source-traced repairs that actually stop the leak." },
  { slug: "/emergency-roof-leak-repair", title: "Emergency Roof Leak Repair", blurb: "Same-day triage and tarp service during monsoon." },
  { slug: "/tile-roofing", title: "Tile Roofing", blurb: "Concrete and clay tile installs, repairs, and underlayment." },
  { slug: "/shingle-roofing", title: "Shingle Roofing", blurb: "Architectural and impact-rated asphalt shingle systems." },
  { slug: "/flat-roof-repair", title: "Flat Roof Repair", blurb: "TPO, mod-bit, and BUR repair plus ponding corrections." },
  { slug: "/skylight-repair", title: "Skylight Repair", blurb: "Curb flashings, gaskets, and full unit replacements." },
  { slug: "/roof-maintenance", title: "Roof Maintenance", blurb: "Pre-monsoon inspections and sealant refresh plans." },
  { slug: "/roof-inspections-for-realtors", title: "Roof Inspections for Realtors", blurb: "Fast, detailed transaction inspections for agents." },
  { slug: "/commercial-roofing", title: "Commercial Roofing", blurb: "SPF, TPO, mod-bit, and BUR systems for commercial buildings." },
  { slug: "/hoa-roofing", title: "HOA Roofing", blurb: "Multi-unit re-roofs, reserve studies, and warranty service." },
  { slug: "/apartment-roofing", title: "Apartment & Condo Roofing", blurb: "Multifamily roofing for owners and property managers." },
];

const businessSchema = {
  "@context": "https://schema.org",
  "@type": "RoofingContractor",
  "@id": "https://www.durafoaminc.com/#business",
  name: "Durafoam Roofing",
  url: "https://www.durafoaminc.com",
  telephone: "+14809415373",
  address: {
    "@type": "PostalAddress",
    streetAddress: "2810 E Mohawk Ln",
    addressLocality: "Phoenix",
    addressRegion: "AZ",
    postalCode: "85050",
    addressCountry: "US",
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Roofing services",
    itemListElement: services.map((s) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name: s.title, url: canonical(s.slug) },
    })),
  },
};

export const Route = createFileRoute("/services")({
  loader: () => loadPageSeo("services"),
  head: ({ loaderData }) => applySeoOverrides({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: canonical(SLUG) },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: canonical(SLUG) }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(businessSchema) },
    ],
  }, loaderData),
  component: ServicesPage,
});

function ServicesPage() {
  return (
    <>
      <section className="bg-primary text-white">
        <Container className="py-14 lg:py-20">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white">
            Our Services
          </p>
          <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-[1.15] sm:text-4xl lg:text-5xl">
            Roofing services for Phoenix homes and buildings
          </h1>
          <p className="mt-4 max-w-2xl text-base text-white/85 sm:text-lg">
            From a single leak repair to a full multifamily re-roof program — we work on every
            common Phoenix roof type and have been doing it since 1989.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <ButtonLink href="tel:+14809415373" variant="primary" size="lg">
              <Phone className="h-4 w-4" aria-hidden="true" /> 480-941-5373
            </ButtonLink>
          </div>
        </Container>
      </section>

      <section className="bg-background py-14 lg:py-20">
        <Container>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s) => (
              <Link
                key={s.slug}
                to={s.slug}
                className="group flex flex-col rounded-2xl border border-border bg-white p-6 shadow-[0_2px_8px_rgba(15,26,66,0.04)] transition-all hover:-translate-y-0.5 hover:border-accent hover:shadow-[0_10px_30px_-12px_rgba(15,26,66,0.18)]"
              >
                <h2 className="text-lg font-semibold text-primary">{s.title}</h2>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground/80">{s.blurb}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:text-accent">
                  Learn more about {s.title.toLowerCase()}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-[var(--primary-deep)] py-14 text-white lg:py-16">
        <Container className="text-center">
          <h2 className="mx-auto max-w-3xl text-2xl font-bold sm:text-3xl">
            Not sure which service you need?
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-white/80">
            We'll inspect your roof and tell you honestly what it needs — at no cost.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <ButtonLink href="tel:+14809415373" variant="primary" size="lg">
              <Phone className="h-4 w-4" aria-hidden="true" /> Call 480-941-5373
            </ButtonLink>
          </div>
        </Container>
      </section>
    </>
  );
}
